import { ENTITIES, type ImportKind, type Role, type UserDto } from '@cockpit/shared';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  ErrorState,
  Field,
  Modal,
  Notice,
  Select,
  Spinner,
  Table,
  Tabs,
  Td,
  Th,
} from '@cockpit/ui';
import { useQueryClient } from '@tanstack/react-query';
import { Info, RefreshCw, Upload } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { PageHeader } from '../components/Layout.js';
import { api, ApiError } from '../lib/api.js';
import { dateTime } from '../lib/format.js';
import { ROLE_LABEL } from '../lib/labels.js';
import { useImports, useMe, useSyncRuns, useUsers } from '../lib/queries.js';

const STATUS_TONE = { ok: 'success', error: 'danger', running: 'azur', pending: 'neutral' } as const;
const STATUS_LABEL = { ok: 'Terminé', error: 'Échec', running: 'En cours', pending: 'En attente' } as const;

type Tab = 'imports' | 'sync' | 'users';

export function AdminPage() {
  const [tab, setTab] = useState<Tab>('imports');
  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        eyebrow="Administration"
        title="Données et accès"
        sub="Imports, synchronisation avec Flex et droits des utilisateurs."
      />
      <Tabs
        label="Rubrique"
        value={tab}
        onChange={setTab}
        tabs={[
          { key: 'imports', label: 'Imports' },
          { key: 'sync', label: 'Synchronisation' },
          { key: 'users', label: 'Utilisateurs' },
        ]}
      />
      {tab === 'imports' && <Imports />}
      {tab === 'sync' && <Sync />}
      {tab === 'users' && <Users />}
    </div>
  );
}

const IMPORT_KINDS: Record<ImportKind, { label: string; short: string; hint: string; accept: string }> = {
  'cockpit-xlsx': {
    label: 'Cockpit FIN — classeur CEX (Excel)',
    short: 'Cockpit (CEX)',
    hint: 'Fichier .xlsx, un onglet REC par société.',
    accept: '.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  },
  'cockpit-json': {
    label: 'Cockpit (FIN et OPE) — JSON',
    short: 'Cockpit',
    hint: 'Format JSON, même structure que celle servie par l’API.',
    accept: '.json,application/json',
  },
  'plan-json': {
    label: 'Plan d’action complet — JSON',
    short: 'Plan d’action',
    hint: 'Format JSON, même structure que celle servie par l’API.',
    accept: '.json,application/json',
  },
};

function Imports() {
  const q = useImports();
  const qc = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const [kind, setKind] = useState<ImportKind>('cockpit-xlsx');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Le traitement se fait en arrière-plan : on recharge les données dès qu'un import aboutit.
  const lastOk = q.data?.find((r) => r.status === 'ok')?.id;
  useEffect(() => {
    if (!lastOk) return;
    qc.invalidateQueries({ queryKey: ['cockpit'] });
    qc.invalidateQueries({ queryKey: ['plan'] });
  }, [lastOk, qc]);

  const upload = async (file: File) => {
    setBusy(true);
    setError(null);
    const form = new FormData();
    form.append('kind', kind);
    form.append('file', file);
    try {
      await api('/api/admin/imports', { method: 'POST', form });
      await qc.invalidateQueries({ queryKey: ['imports'] });
      qc.invalidateQueries({ queryKey: ['cockpit'] });
      qc.invalidateQueries({ queryKey: ['plan'] });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Import impossible.');
    } finally {
      setBusy(false);
      if (input.current) input.current.value = '';
    }
  };

  return (
    <div className="grid gap-6 lg:grid-cols-[360px_minmax(0,1fr)]">
      <Card className="self-start">
        <CardHeader eyebrow="Nouveau" title="Importer un fichier" />
        <div className="flex flex-col gap-4">
          <Field label="Type de données" hint={IMPORT_KINDS[kind].hint}>
            {(id) => (
              <Select id={id} value={kind} onChange={(e) => setKind(e.target.value as ImportKind)}>
                {Object.entries(IMPORT_KINDS).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v.label}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <input
            ref={input}
            type="file"
            accept={IMPORT_KINDS[kind].accept}
            className="sr-only"
            onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])}
          />
          <Button
            icon={<Upload size={18} strokeWidth={1.75} />}
            disabled={busy}
            onClick={() => input.current?.click()}
          >
            {busy ? 'Envoi…' : 'Choisir un fichier'}
          </Button>
          {error && <ErrorState title="Import refusé">{error}</ErrorState>}
          <Notice icon={<Info size={18} strokeWidth={1.75} />}>
            {kind === 'cockpit-xlsx' ? (
              <>
                Déposez le classeur « CEX GROUPE ODICE + CONSO » complet : les onglets <code>REC</code> de
                chaque société (CA, marge, REX, masse salariale, dernier budget de l’année) remplacent les
                données comptables du cockpit à chaque import.
              </>
            ) : (
              <>
                Fichier cockpit :{' '}
                <code>
                  {'{ "asOf": "AAAA-MM-JJ", "kind": "fin" | "ope" | "both", "data": { "MSE": { … } } }'}
                </code>
                . Les clôtures comptables (CEX, budget, bilans) et la balance âgée ne sont pas disponibles
                dans SYNC : elles passent par cet import.
              </>
            )}
          </Notice>
        </div>
      </Card>
      <Card padded={false} className="px-2 py-3">
        <h3 className="px-3 pb-2 font-display text-base font-bold text-ink">Derniers imports</h3>
        {q.isLoading ? (
          <Spinner className="p-3" />
        ) : !q.data?.length ? (
          <div className="p-3">
            <EmptyState title="Aucun import">
              Les données affichées proviennent du jeu initial ou de la synchronisation.
            </EmptyState>
          </div>
        ) : (
          <Table minWidth={640}>
            <thead>
              <tr>
                <Th>Fichier</Th>
                <Th>Type</Th>
                <Th>Statut</Th>
                <Th align="right">Lignes</Th>
                <Th>Déposé</Th>
              </tr>
            </thead>
            <tbody>
              {q.data.map((r) => (
                <tr key={r.id}>
                  <Td className="max-w-[240px]">
                    <span className="block truncate font-semibold">{r.fileName}</span>
                    {r.message && <span className="block text-[12.5px] text-danger-700">{r.message}</span>}
                  </Td>
                  <Td>{IMPORT_KINDS[r.kind as ImportKind]?.short ?? 'Cockpit'}</Td>
                  <Td>
                    <Badge tone={STATUS_TONE[r.status]} dot>
                      {STATUS_LABEL[r.status]}
                    </Badge>
                  </Td>
                  <Td align="right">{r.rows ?? '—'}</Td>
                  <Td className="text-ink-2">
                    {dateTime(r.createdAt)} · {r.by}
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </div>
  );
}

function Sync() {
  const q = useSyncRuns();
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const run = async () => {
    setError(null);
    try {
      await api('/api/admin/sync', { method: 'POST' });
      qc.invalidateQueries({ queryKey: ['sync'] });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Lancement impossible.');
    }
  };
  if (q.isLoading) return <Spinner />;
  const s = q.data!;
  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader
          eyebrow="Plan d’action"
          title="Synchronisation avec Flex (base SYNC)"
          actions={
            <Button icon={<RefreshCw size={18} strokeWidth={1.75} />} disabled={!s.configured} onClick={run}>
              Relancer la synchronisation
            </Button>
          }
        />
        {s.configured ? (
          <p className="text-[14px] text-ink-2">
            Synchronisation automatique planifiée (<code>{s.cron}</code>, heure de Paris), après la
            réplication nocturne de Flex.
          </p>
        ) : (
          <Notice icon={<Info size={18} strokeWidth={1.75} />}>
            La variable <code>SYNC_DATABASE_URL</code> n’est pas renseignée : le plan d’action reste sur le
            dernier import ou le jeu initial.
          </Notice>
        )}
        {error && (
          <div className="mt-3">
            <ErrorState>{error}</ErrorState>
          </div>
        )}
      </Card>
      <Card padded={false} className="px-2 py-3">
        {!s.runs.length ? (
          <div className="p-3">
            <EmptyState title="Aucune synchronisation">
              Aucune exécution enregistrée pour le moment.
            </EmptyState>
          </div>
        ) : (
          <Table minWidth={560}>
            <thead>
              <tr>
                <Th>Début</Th>
                <Th>Statut</Th>
                <Th align="right">Affaires</Th>
                <Th>Message</Th>
              </tr>
            </thead>
            <tbody>
              {s.runs.map((r) => (
                <tr key={r.id}>
                  <Td>{dateTime(r.startedAt)}</Td>
                  <Td>
                    <Badge tone={STATUS_TONE[r.status]} dot>
                      {STATUS_LABEL[r.status]}
                    </Badge>
                  </Td>
                  <Td align="right">{r.rows ?? '—'}</Td>
                  <Td className="text-ink-2">{r.message ?? ''}</Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </div>
  );
}

function Users() {
  const q = useUsers();
  const me = useMe();
  const [edit, setEdit] = useState<UserDto | null>(null);
  const [remove, setRemove] = useState<UserDto | null>(null);
  /** Raison pour laquelle un compte ne peut pas être supprimé, ou null. */
  const lockReason = (u: UserDto): string | null =>
    u.id === me.data?.id
      ? 'Vous ne pouvez pas supprimer votre propre compte.'
      : u.protected
        ? 'Compte administrateur local indépendant du SSO : il ne peut pas être supprimé.'
        : null;
  if (q.isLoading) return <Spinner />;
  if (q.error) return <ErrorState>{(q.error as Error).message}</ErrorState>;
  return (
    <>
      <Card padded={false} className="px-2 py-3">
        <Table minWidth={720}>
          <thead>
            <tr>
              <Th>Utilisateur</Th>
              <Th>Rôle</Th>
              <Th>Entités</Th>
              <Th>Connexion</Th>
              <Th />
            </tr>
          </thead>
          <tbody>
            {q.data!.map((u) => (
              <tr key={u.id}>
                <Td>
                  <span className="block font-semibold">{u.name}</span>
                  <span className="block text-[12.5px] text-ink-3">
                    {u.email} · {u.provider === 'entra' ? 'Microsoft' : 'compte local'}
                  </span>
                </Td>
                <Td>{ROLE_LABEL[u.role]}</Td>
                <Td className="text-ink-2">
                  {u.entities.length
                    ? u.entities.join(', ')
                    : u.role === 'da'
                      ? 'Aucune (accès non configuré)'
                      : 'Toutes'}
                </Td>
                <Td className="text-ink-2">{u.lastLoginAt ? dateTime(u.lastLoginAt) : 'jamais'}</Td>
                <Td align="right">
                  <span className="inline-flex gap-2">
                    <Button size="sm" variant="secondary" onClick={() => setEdit(u)}>
                      Modifier
                    </Button>
                    <Button
                      size="sm"
                      variant="dangerOutline"
                      disabled={!!lockReason(u)}
                      title={lockReason(u) ?? undefined}
                      onClick={() => setRemove(u)}
                    >
                      Supprimer
                    </Button>
                  </span>
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Card>
      {edit && <EditUser user={edit} onClose={() => setEdit(null)} />}
      {remove && <DeleteUser user={remove} onClose={() => setRemove(null)} />}
    </>
  );
}

function DeleteUser({ user, onClose }: { user: UserDto; onClose: () => void }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      await api(`/api/admin/users/${user.id}`, { method: 'DELETE' });
      await qc.invalidateQueries({ queryKey: ['users'] });
      qc.invalidateQueries({ queryKey: ['plan'] });
      onClose();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Suppression impossible.');
      setBusy(false);
    }
  };
  return (
    <Modal
      open
      onClose={onClose}
      title="Supprimer ce compte ?"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Annuler
          </Button>
          <Button variant="danger" disabled={busy} onClick={confirm}>
            {busy ? 'Suppression…' : 'Supprimer le compte'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3 text-[14px] text-ink-2">
        <p>
          Le compte de <strong className="text-ink">{user.name}</strong> ({user.email}) sera supprimé
          définitivement. Cette personne ne pourra plus se connecter avec ce compte.
        </p>
        <p>
          Les affaires qu’elle a marquées « traitées » et ses imports sont conservés, attribués à « Compte
          supprimé ».
        </p>
        {user.provider === 'entra' && (
          <p>
            Son compte Microsoft n’est pas supprimé : si elle se reconnecte, un nouveau compte sera créé, sans
            aucune entité attribuée.
          </p>
        )}
        {error && <ErrorState title="Suppression impossible">{error}</ErrorState>}
      </div>
    </Modal>
  );
}

function EditUser({ user, onClose }: { user: UserDto; onClose: () => void }) {
  const qc = useQueryClient();
  const [role, setRole] = useState<Role>(user.role);
  const [ents, setEnts] = useState<string[]>(user.entities);
  const [error, setError] = useState<string | null>(null);
  const save = async () => {
    try {
      await api(`/api/admin/users/${user.id}`, { method: 'PATCH', body: { role, entities: ents } });
      await qc.invalidateQueries({ queryKey: ['users'] });
      onClose();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Enregistrement impossible.');
    }
  };
  return (
    <Modal
      open
      onClose={onClose}
      title={user.name}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Annuler
          </Button>
          <Button onClick={save}>Enregistrer</Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label="Rôle">
          {(id) => (
            <Select id={id} value={role} onChange={(e) => setRole(e.target.value as Role)}>
              {(Object.keys(ROLE_LABEL) as Role[]).map((r) => (
                <option key={r} value={r}>
                  {ROLE_LABEL[r]}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <fieldset>
          <legend className="mb-2 text-[13px] font-semibold text-ink">Entités visibles</legend>
          <p className="mb-2 text-[13px] text-ink-3">
            Administrateur : toujours toutes les entités. Direction : toutes si aucune case n’est cochée.
            Directeur d’agence : uniquement les entités cochées, aucune par défaut.
          </p>
          <div className="grid grid-cols-2 gap-2">
            {ENTITIES.filter((e) => e.inPlan).map((e) => (
              <label key={e.sync} className="flex items-center gap-2 text-[14px] text-ink">
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-[rgb(var(--c-navy-600))]"
                  checked={ents.includes(e.sync)}
                  onChange={(ev) =>
                    setEnts(ev.target.checked ? [...ents, e.sync] : ents.filter((x) => x !== e.sync))
                  }
                />
                {e.label}
              </label>
            ))}
          </div>
        </fieldset>
        {error && <ErrorState>{error}</ErrorState>}
      </div>
    </Modal>
  );
}
